import { createSlice } from '@reduxjs/toolkit'
import _ from 'lodash'
import config from 'configs/config'
import { parseJSON, SS } from 'utils/common'
import { ModelExchangeVariantItem } from 'models/modelExchngeVariatnItem'

const steps = config.flowSteps

/**
 * getFlowSteps {Function}
 * @description generate proper steps for low stepper component based on server side response
 * @return {Array}
 *
 * */
const getFlowSteps = (items, state) => {
	let flowSteps = []
	let sortedFlowSteps = []
	let { SELECT, RETURN, COMPLAINT, EXCHANGE, SHIPPING, REFUND } = steps
	items.forEach((item) => {
		flowSteps.push(...item.flowSteps)
	})
	let uniqueFlowSteps = _.uniq(flowSteps)

	if (uniqueFlowSteps.includes(SELECT)) sortedFlowSteps.push(SELECT)
	if (uniqueFlowSteps.includes(RETURN)) sortedFlowSteps.push(RETURN)
	if (uniqueFlowSteps.includes(COMPLAINT)) sortedFlowSteps.push(COMPLAINT)
	if (uniqueFlowSteps.includes(EXCHANGE)) sortedFlowSteps.push(EXCHANGE)
	if (uniqueFlowSteps.includes(SHIPPING)) sortedFlowSteps.push(SHIPPING)
	if (uniqueFlowSteps.includes(REFUND)) sortedFlowSteps.push(REFUND)

	return sortedFlowSteps && sortedFlowSteps.length
		? sortedFlowSteps.map((item) => {
				return {
					name: item,
					status: item === state.flowStep ? true : false,
				}
		  })
		: state.steps
}

/**
 * setActiveStep {Function}
 * @description set proper active status to the step with specific name
 * @return {Array}
 *
 * */
const setActiveStep = (steps, flowName) => {
	return steps.map((step) => ({
		name: step.name,
		status: step.name === flowName,
	}))
}
/**
 * setActiveStepInPartialRequestData {Function}
 * @description set proper active status to the partialRequestData with specific name
 * @return {Array}
 *
 * */
const setActiveStepInPartialRequestData = (items, flowName) => {
	return items?.map((item) => ({
		...item,
		flowStep: flowName,
	}))
}

/**
 * getItemsWithReasonCondition {Function}
 * @description - set an appropriate return reason and condition to the specific item based on id and data in payload
 * @return {Object}
 * */
const getItemsWithReasonCondition = (payload, stateItems) => {
	for (let key in stateItems) {
		let currentFlow = stateItems[key]
		for (let jey in currentFlow) {
			if (jey === payload.itemIndex) {
				currentFlow[jey][payload.attrType] = payload.attrValue
				if (payload.attrType === 'reclamationReasonApiKey') {
					let shopProperties = SS.get('shopProperties')
					let reasonsToSkip =
						shopProperties['widget.skip.exchange.return.reasons'] ||
						[]
					currentFlow[jey][
						'IsExchangeMandatory'
					] = !reasonsToSkip.includes(payload.attrValue)
				}
			}
		}
	}

	return stateItems
}

/**
 * getItemsWithComment {Function}
 * @description - set an appropriate return reason and condition to the specific item based on id and data in payload
 * @return {Object}
 * */
const getItemsWithComment = (payload, stateItems) => {
	for (let key in stateItems) {
		let currentFlow = stateItems[key]
		for (let jey in currentFlow) {
			if (jey === payload.itemIndex) {
				currentFlow[jey]['comment'] = payload.comment
			}
		}
	}
	return stateItems
}

/**
 * getItemsWithImages {Function}
 * @description - set an appropriate return images to the specific item based on id and data in payload
 * @return {Object}
 * */
const getItemsWithImages = (payload, stateItems) => {
	for (let key in stateItems) {
		let currentFlow = stateItems[key]
		for (let jey in currentFlow) {
			if (jey === payload.itemIndex) {
				currentFlow[jey]['reclamationImages'] = payload.images
			}
		}
	}
	return stateItems
}

/**
 * generatePartialRequestData {Function}
 * @description - each time when items changed this function generated new data for partial request to the server
 * @return {Object || Null}
 * */
const generatePartialRequestData = (items, flowStep, shipmentOptions = []) => {
	let flowItems = []
	let originalOrderExternalId
	let currentShopId = ''
	for (let [currentReturnAction, currentFlowItems] of Object.entries(items)) {
		if (
			!_.isEmpty(currentFlowItems) &&
			currentReturnAction !== 'newExchange'
		) {
			for (let [itemId, currentItem] of Object.entries(
				currentFlowItems
			)) {
				if (!_.isEmpty(currentItem)) {
					flowItems.push({
						orderItemId: +itemId,
						returnAction: currentReturnAction.toUpperCase(),
						flowStep: flowStep.toUpperCase(),
						reclamationReasonApiKey:
							currentItem?.reclamationReasonApiKey || null,
						returnOrderItemConditionApiKey:
							currentItem?.returnOrderItemConditionApiKey || null,
						comment: currentItem?.comment || null,
						reclamationImages: currentItem?.reclamationImages || [],
					})
					if (!originalOrderExternalId) {
						originalOrderExternalId =
							currentFlowItems[itemId].orderExternalId
					}
					if (!currentShopId) {
						currentShopId = currentFlowItems[itemId].shopId
					}
				}
			}
		}
	}

	if (flowItems.length) {
		let requestObject = {
			shopId: currentShopId, //+SS.get('shopId')
			language: SS.get('language') || 'en',
			fetchProductDetails: flowStep === config.flowSteps.SELECT,
			returnOrder: {
				items: flowItems,
				originalOrderExternalId,
			},
		}
		// if we have selected option
		if (shipmentOptions?.length) {
			let selectedOption = _.find(shipmentOptions, (option) => {
				return option.isSelected
			})
			requestObject.shippingOptionId = selectedOption?.id || null
			requestObject.shippingOptionLabelsAmount =
				selectedOption?.shippingOptionLabelsAmount || null
		}
		return requestObject
	} else {
		return null
	}
}
/**
 * generateNewOrderExchangeItem {Function}
 * @description - add exchange item in partial request to the server and set fetchProductDetails to false
 * @return {Object}
 * */
const generateNewOrderExchangeItem = (newOrders, partialData) => {
	if (Object.keys(newOrders).length) {
		return {
			...partialData,
			fetchProductDetails: false,
			newOrder: {
				items: Object.values(newOrders),
			},
		}
	} else {
		if (partialData && partialData.newOrder) {
			delete partialData.newOrder
		}
	}

	return partialData
}

/**
 * getSelectedShippingOption {Function}
 * @description - get items with selected shipping option
 * @return {Array}
 * */
const getSelectedShippingOption = (payload, shipmentOptions) => {
	let { labels = 1, id } = payload

	shipmentOptions.forEach((option) => {
		if (option.id === id) {
			option.isSelected = true
			option.shippingOptionLabelsAmount = labels
		} else {
			option.isSelected = false
			option.shippingOptionLabelsAmount = 0
		}
	})

	return shipmentOptions
}

/**
 * isSomeAttributeSingle {Function}
 * @description - define is some of the attribute have a single value. Define the name of such attribute.
 * @return {Object}
 * */
const isSomeAttributeSingle = (attr) => {
	let isSingle = false
	let name = ''
	let value = ''

	for (let key in attr) {
		if (attr.hasOwnProperty(key)) {
			let currentAttr = attr[key]
			if (currentAttr && currentAttr.length === 1) {
				isSingle = true
				name = key
				value = currentAttr[0]['value']
			}
		}
	}

	return {
		isSingle,
		name,
		value,
	}
}

/**
 * generateVariantsLists {Function}
 * @description - converting an array into an object with keys identifiers for display on the exchange page. It also generates arrays of unique options for each product attribute.
 * @return {Object}
 * */
const generateVariantsLists = (variants, currency) => {
	let variantsList = {}
	let options = {}
	//if (variants.length > 1) {
	variants.forEach((variant) => {
		let keysVariantArr = []
		let attr = Object.entries(parseJSON(variant.attributes))

		for (let [key, obj] of attr) {
			const formatKey = key.toLowerCase().trim().replace(/\s/g, '-') //+'Options'

			// if we have value, we add this option in list
			if (obj.value) {
				const formatValue = obj.value
					.toLowerCase()
					.trim()
					.replace(/\s/g, '-')
				keysVariantArr.push(formatValue)

				// if we dont have options key by default
				if (!options[formatKey]) {
					options[formatKey] = {}
				}

				// if we dont have options key by default and value
				if (!options[formatKey][formatValue]) {
					options[formatKey][formatValue] = {
						label: obj.value,
						value: formatValue,
						outOfStock: false,
					}
				}
			}
		}

		// We set the flag in the input for the correct display
		// of the drop-down list when the default value of one of the options
		keysVariantArr.forEach((i) => {
			variantsList[i] = ModelExchangeVariantItem({
				...variant,
				stockStatus: variant.stockStatus,
			})

			//console.log(i, keysVariantArr.join('-'), variant.stockStatus)
		})

		variantsList[keysVariantArr.join('-')] = ModelExchangeVariantItem({
			...variant,
			currency,
		})
	})

	for (let [key, option] of Object.entries(options)) {
		options[key] = Object.values(option)
	}
	//}

	//in case of one attribute we need to set outOfStock attribute for other attributes from the very beginning
	let isSingleAttribute = isSomeAttributeSingle(options)

	if (isSingleAttribute.isSingle) {
		for (let optKey in options) {
			if (options.hasOwnProperty(optKey)) {
				if (isSingleAttribute.name !== optKey) {
					let currentOptions = options[optKey]
					currentOptions.forEach((item) => {
						let var1 =
							variantsList[
								isSingleAttribute.value + '-' + item.value
							]
						let var2 =
							variantsList[
								item.value + '-' + isSingleAttribute.value
							]

						if (var1) {
							item.outOfStock =
								var1.stockStatus === 'IN_STOCK' ? false : true
						}
						if (var2) {
							item.outOfStock =
								var2.stockStatus === 'IN_STOCK' ? false : true
						}
					})
				}
			}
		}
	}

	return {
		variants: variantsList,
		options,
	}
}
const getNextFlowStep = (stepsList, currentFlow) => {
	let nextStep = null
	stepsList.forEach((step, idx) => {
		if (currentFlow === step.name) {
			let currentStep = stepsList[idx + 1]?.name
			nextStep = currentStep ? currentStep : null
		}
	})
	return nextStep?.toLowerCase()
}
const getPrevFlowStep = (stepsList, currentFlow) => {
	let prevStep = null
	stepsList.forEach((step, idx) => {
		if (currentFlow === step.name) {
			let a = stepsList[idx - 1]?.name
			prevStep = a ? a : null
		}
	})
	return prevStep?.toLowerCase()
}
const clearItemsFlowKeyVariant = (items) => {
	Object.keys(items).forEach((flow) => {
		if (flow !== 'newExchange')
			Object.keys(items[flow]).forEach((key) => {
				items[flow][key].keyVariant = ''
			})
	})

	return items
}
const whoFlow = (orderItemId, items) => {
	if (items.refund[orderItemId]) {
		return 'refund'
	}
	if (items.reclamation[orderItemId]) {
		return 'reclamation'
	}
	if (items.exchange[orderItemId]) {
		return 'exchange'
	}
	return null
}
const totalDefault = {
	exchangeTotal: 0,
	grossShippingRefund: 0,
	paymentTotal: 0,
	refundTotal: 0,
	returnFee: 0,
	returnFeeTax: 0,
	returnTotal: 0,
	shippingFee: 0,
	shippingFeeTax: 0,
}

export const stepsProviderSlice = createSlice({
	name: 'stepsProvider',
	initialState: SS.get('steps') || {
		steps: [{ name: 'SELECT', status: true }],
		totals: totalDefault,
		shipmentOptions: [],
		itemConditions: [],
		reclamationReasons: [],
		returnReasons: [],
		step: 0,
		wasReasonAdded: {},
		flowStep: 'SELECT',
		setFlow: null,
		nextFlowStep: null,
		prevFlowStep: null,
		items: {
			refund: {},
			reclamation: {},
			exchange: {},
			newExchange: {},
		},
		partialRequestData: null,
		faqs: [],
		isPartialErrorPopupActive: false,
		errorMessage: null,
	},
	reducers: {
		getStepsProviderStart: (state) => {
			state.isLoading = true
		},
		getStepsProviderSuccess: (state, { payload }) => {
			let {
				//itemConditions,
				//reclamationReasons,
				//returnReasons,
				shipmentOptions,
			} = state
			let { items } = payload

			items.forEach((item) => {
				const { flowSteps, orderItemId } = item
				let refund = state?.items?.refund[orderItemId]
				let reclamation = state?.items?.reclamation[orderItemId]
				let exchange = state?.items?.exchange[orderItemId]

				if (!!refund) refund.flowSteps = flowSteps
				if (!!reclamation) reclamation.flowSteps = flowSteps
				if (!!exchange) exchange.flowSteps = flowSteps
			})

			state.steps = getFlowSteps(payload.items, state)
			state.nextFlowStep = getNextFlowStep(state.steps, state.flowStep)
			state.prevFlowStep = getPrevFlowStep(state.steps, state.flowStep)
			state.totals = payload.totals

			/*TODO: Question for Ruslan about this IF*/
			if (payload.itemConditions /*!itemConditions?.length*/) {
				state.itemConditions = payload.itemConditions
			}
			if (payload.reclamationReasons /*!reclamationReasons?.length*/) {
				state.reclamationReasons = payload.reclamationReasons
			}
			if (payload.returnReasons /*!returnReasons?.length*/) {
				state.returnReasons = payload.returnReasons
			}
			if (!shipmentOptions?.length) {
				state.shipmentOptions = payload.shipmentOptions || []
			}

			if (state.prevFlowStep === config.flowSteps.SELECT.toLowerCase()) {
				let a = payload?.items?.forEach((item) => {
					let { orderItemId, variants, description } =
						item?.product || {}
					if (variants?.length) {
						let flow = whoFlow(orderItemId, state.items)
						if (flow) {
							state.items[flow][orderItemId] = {
								...state.items[flow][orderItemId],
								...generateVariantsLists(
									variants,
									state.items[flow][orderItemId].currency
								),
								description,
							}
						}
					}
				})
			}
			state.isLoading = false
		},
		getStepsProviderError: (state) => {
			state.isLoading = false
		},
		getStep: (state, { payload }) => {
			const { step } = payload
			state.step = step
		},
		setStepsToDefault: (state) => {
			state.steps = [{ name: steps.SELECT, status: true }]
		},
		setFlowStep: (state, { payload }) => {
			console.log('flowStep =>', payload)

			state.flowStep = payload
			state.steps = setActiveStep(state.steps, payload)

			state.nextFlowStep = getNextFlowStep(state.steps, state.flowStep)
			state.prevFlowStep = getPrevFlowStep(state.steps, state.flowStep)

			let itemsPartialReqData =
				state.partialRequestData?.returnOrder?.items

			state.partialRequestData = state.partialRequestData && {
				...state.partialRequestData,
				returnOrder: {
					...state.partialRequestData.returnOrder,
					items: setActiveStepInPartialRequestData(
						itemsPartialReqData,
						payload
					),
				},
			}

			//clear exchange data item
			if (
				state.flowStep === 'SELECT' ||
				state.flowStep === 'RETURN' ||
				state.flowStep === 'COMPLAINT'
			) {
				delete state?.partialRequestData?.newOrder
				state.items.newExchange = {}
				state.items = clearItemsFlowKeyVariant(state.items)
			}

			state.isLoading = false
		},
		setItemFlow: (state, { payload }) => {
			const flow = payload.flow
			const key = payload.item.orderItemId

			for (let i in state.items) {
				if (i !== flow && state.items[i][key]) {
					delete state.items[i][key]
				}
			}
			state.currentItemFlow = payload.item
			if (!state.items[flow][key]) {
				state.items[flow][key] = {
					...payload.item,
					setFlow: payload.flow,
					reclamationReasonApiKey: null,
					returnOrderItemConditionApiKey: null,
				}
			}

			state.partialRequestData = generatePartialRequestData(
				state.items,
				state.flowStep
			)
			state.isLoading = false
			state.wasReasonAdded[key] = false
		},
		removeItemFlow: (state, { payload }) => {
			const flow = payload.flow
			const key = payload.orderItemId

			if (state.items[flow] && state.items[flow][key]) {
				delete state.items[flow][key]
				if (flow === 'exchange') {
					if (
						state.items['newExchange'] &&
						state.items['newExchange'][key]
					) {
						delete state.items['newExchange'][key]
					}
				}
			}
			state.partialRequestData = generatePartialRequestData(
				state.items,
				state.flowStep
			)
		},
		setReasonCondition: (state, { payload }) => {
			state.items = getItemsWithReasonCondition(payload, state.items)
			state.partialRequestData = generatePartialRequestData(
				state.items,
				state.flowStep
			)
			if (payload.attrType === 'reclamationReasonApiKey') {
				if (!state.wasReasonAdded[payload.orderItemId]) {
					state.wasReasonAdded[payload.orderItemId] = true
				}
			}
		},
		setReclamationComment: (state, { payload }) => {
			state.items = getItemsWithComment(payload, state.items)
			state.partialRequestData = generatePartialRequestData(
				state.items,
				state.flowStep
			)
		},
		setReclamationImages: (state, { payload }) => {
			let items = getItemsWithImages(payload, state.items)
			state.items = items
			state.partialRequestData = generatePartialRequestData(
				items,
				state.flowStep
			)
		},
		clearWasReasonAdded: (state, { payload }) => {
			state.wasReasonAdded = 0
		},
		setItemExchange: (state, { payload }) => {
			const { key, itemEx } = payload

			let refund = state?.items?.refund[itemEx.orderItemId]
			let reclamation = state?.items?.reclamation[itemEx.orderItemId]
			let exchange = state?.items?.exchange[itemEx.orderItemId]

			if (!!refund) refund.keyVariant = key
			if (!!reclamation) reclamation.keyVariant = key
			if (!!exchange) exchange.keyVariant = key

			state.items.newExchange[itemEx.orderItemId] = itemEx
			state.partialRequestData = generateNewOrderExchangeItem(
				state.items.newExchange,
				state.partialRequestData
			)
			state.isLoading = false
		},
		removeItemExchanges: (state, { payload }) => {
			if (state.items.newExchange[payload]) {
				delete state.items.newExchange[payload]
				if (state.items.exchange[payload]) {
					state.items.exchange[payload].keyVariant = ''
				}
				if (state.items.reclamation[payload]) {
					state.items.reclamation[payload].keyVariant = ''
				}
				if (state.items.refund[payload]) {
					state.items.refund[payload].keyVariant = ''
				}
			}
			state.partialRequestData = generateNewOrderExchangeItem(
				state.items.newExchange,
				state.partialRequestData
			)
		},
		setShippingOption: (state, { payload }) => {
			state.shipmentOptions = getSelectedShippingOption(
				payload,
				state.shipmentOptions
			)
			state.partialRequestData = generatePartialRequestData(
				state.items,
				state.flowStep,
				state.shipmentOptions
			)
			state.partialRequestData = generateNewOrderExchangeItem(
				state.items.newExchange,
				state.partialRequestData
			)
		},
		cleanItems: (state, { payload }) => {
			state.steps = [{ name: 'SELECT', status: true }]
			state.partialRequestData = null
			state.wasReasonAdded = {}
			state.items = {
				refund: {},
				reclamation: {},
				exchange: {},
				newExchange: {},
			}
			state.totals = totalDefault
			state.items = clearItemsFlowKeyVariant(state.items)
		},
		setFAQs: (state, { payload }) => {
			state.faqs = payload
			state.isLoading = false
		},
		showPartialErrorPopup: (state, { payload }) => {
			state.isLoading = false
			state.isPartialErrorPopupActive = false
			state.errorMessage = payload.message || 'unknown error'
		},
		closePartialErrorPopup: (state, { payload }) => {
			state.isPartialErrorPopupActive = false
			state.errorMessage = null
		},
		deletePartialNewOrder: (state) => {
			delete state?.partialRequestData?.newOrder
			state.items.newExchange = {}
			state.items = clearItemsFlowKeyVariant(state.items)
		},
		clearShippingOptions: (state) => {
			console.log('clearShippingOptions =>')
			state.shipmentOptions = []
			if (
				state.partialRequestData &&
				state.partialRequestData.shippingOptionId
			) {
				delete state.partialRequestData.shippingOptionId
			}
			if (
				state.partialRequestData &&
				state.partialRequestData.shippingOptionLabelsAmount
			) {
				delete state.partialRequestData.shippingOptionLabelsAmount
			}
		},
	},
})

export const {
	getStepsProviderStart,
	getStepsProviderSuccess,
	getStepsProviderError,
	//getStepProvider,
	setStepsToDefault,
	setFlowStep,
	setReasonCondition,
	setItemFlow,
	removeItemFlow,
	clearWasReasonAdded,
	setReclamationComment,
	setReclamationImages,
	setItemExchange,
	removeItemExchanges,
	setShippingOption,
	cleanItems,
	setFAQs,
	showPartialErrorPopup,
	closePartialErrorPopup,
	deletePartialNewOrder,
	clearShippingOptions,
} = stepsProviderSlice.actions

export const { reducer } = stepsProviderSlice
